import {icon, swapIcon} from "../../common/js/icon";
import {prefersReducedMotion} from "../../common/js/motion";

/**
 * Builds a slide show on top of PhotoSwipe.
 */
export default class SlideShow {

    /**
     *
     * @param photoSwipeGallery PhotoSwipe
     */
    constructor(photoSwipe) {
        if (!window.EventGalleryLightboxConfiguration) {
            return;
        }

        if (!window.EventGalleryLightboxConfiguration.doUseSlideshow) {
            return;
        }

        // a slide show of one image has nothing to show: the lightbox of the single image
        // page and of a single image tag gets no play button (until 7.0.0 it had one, LB-5)
        if (typeof photoSwipe.getNumItems === 'function' && photoSwipe.getNumItems() < 2) {
            return;
        }

        this._photoSwipe = photoSwipe;
        this._SlideShow_Running = false;
        this._currentTimer = null;
        this._SlideShow_Delay = window?.EventGalleryLightboxConfiguration?.slideshowSpeed || 2000 /*ms*/;
        this._SlideShow_Button = null;
        this._photoSwipe.ui.registerElement({
            name: 'playpause-button',
            order: 9,
            isButton: true,
            html: icon('play', 'eg-icon--lg'),
            onInit: (el) => {
                this._SlideShow_Button = el;
                this._adjustNavigation();
            },
             
            onClick: (event, el) => {
                //console.log('slideshow click')
                return this.toggleSlideShowState();
            }
        });

        this._photoSwipe.on('change', () => {
            this._planNextSwitch(false);
        });

        // closing the lightbox ends the show and its timer
        this._photoSwipe.on('destroy', () => {
            this._stopSlideshow();
            this._photoSwipe = null;
        });
        // a key or a press elsewhere stops the show; a press on the button, its icon included, is left
        // to the click, so that one click toggles once
        this._photoSwipe.on('keydown',     (e) => this._stopSlideshowUnlessOnButton(e));
        this._photoSwipe.on('pointerDown', (e) => this._stopSlideshowUnlessOnButton(e));

        // the option starts the slide show with the lightbox, unless the visitor asked for less
        // motion; the play button starts it then (A11Y-12)
        if (window.EventGalleryLightboxConfiguration.doUseAutoplay && !prefersReducedMotion()) {
            this.toggleSlideShowState();
        }
    }

    /**
     *
     * @param delay int
     */
    setDelay(delay) {
        this._SlideShow_Delay = delay;
    }

    /**
     * switches the slide show on/off depending on the current state
     */
    toggleSlideShowState() {

        if (this._SlideShow_Running) {
            this._stopSlideshow();
        } else {
            this._startSlideshow();
        }
    }

    /**
     * The icon in the button is an svg: a press on it has the svg as target, not the button.
     *
     * @param e PhotoSwipe event with the originalEvent
     */
    _stopSlideshowUnlessOnButton(e) {
        const target = e.originalEvent && e.originalEvent.target;
        if (this._SlideShow_Button && target && this._SlideShow_Button.contains(target)) {
            return;
        }
        this._stopSlideshow();
    }

    _stopSlideshow() {
        this._SlideShow_Running = false;
        clearTimeout(this._currentTimer);
        this._currentTimer = null;
        this._adjustNavigation();
    }

    _startSlideshow() {
        this._SlideShow_Running = true;
        this._planNextSwitch(true);
        this._adjustNavigation();
    }

    /**
     * make sure the navigation displays the current slide show state.
     */
    _adjustNavigation() {
        if (this._SlideShow_Button) {
            this._SlideShow_Button.classList.remove(this._SlideShow_Running ? "play" : "pause");
            this._SlideShow_Button.classList.add(this._SlideShow_Running ? "pause" : "play");
            // the glyph follows the state: a pause glyph while the show runs
            swapIcon(this._SlideShow_Button.querySelector('svg'), this._SlideShow_Running ? 'pause' : 'play');
        }
    }

    /**
     * create the timeout for the next switch.
     *
     * @param isForTheFirstSwitch Boolean changes the switch time so the first switch can be faster
     */
    _planNextSwitch(isForTheFirstSwitch)  {
        clearTimeout(this._currentTimer);
        this._currentTimer = setTimeout(() => this._nextSlide(), isForTheFirstSwitch ? this._SlideShow_Delay / 2.0 :this._SlideShow_Delay);
    }

    /**
     * change to the next slide
     */
    _nextSlide() {
        if (this._photoSwipe === null) {
            return;
        }

        if (!this._SlideShow_Running) {
            return;
        }

        this._photoSwipe.next();
    }
}
